import type { CircleFeature } from '../../../../../types/MiniSiteContents';
import Carousel from '../../../common/Carousel';
import ContentWrapper from '../../../common/ContentWrapper';
import ImageWithFallback from '../../../common/ImageWithFallback';
import isAbsoluteUrl from '../../../../core/helpers/isAbsoluteUrl';
import useRelativeUrl from '../../../../core/hooks/useRelativeUrl';
import Markdown from '../../../common/Markdown/Markdown';

type Props = {
  miniSiteContent: CircleFeature;
};

const MiniSiteCircleFeature = ({ miniSiteContent }: Props) => {
  const { contentTitle, contents } = miniSiteContent;
  const { getRelativeUrl } = useRelativeUrl();
  const settings = {
    className: '-mx-4', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-4', // add spaces at both of the sides of cards
    infinite: false,
    slidesToShow: 4,
    draggable: false,
    prevBtnStyle: '-left-3 top-[140px] card-carousel-arrow-btn',
    nextBtnStyle: '-right-3 top-[140px] card-carousel-arrow-btn',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.5,
          arrows: false,
          draggable: true,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.5,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  return (
    <ContentWrapper className="w-full px-0">
      {contentTitle && <p className="pb-6 text-title3 font-bold">{contentTitle}</p>}
      <Carousel
        settings={settings}
        items={contents}
        renderItem={(content, prefetchImages) => {
          const { id, title, description, photoUrl, link } = content;

          return (
            <div key={id} className="flex flex-col gap-3">
              <div className={link ? '' : 'relative py-[50%]'}>
                <ImageWithFallback
                  alt={id.toString()}
                  src={photoUrl || '/fallback-img-3704x2304.png'}
                  className="rounded-full"
                  layout="fill"
                  objectFit="cover"
                  sizes="(max-width: 768px) 50vw, 320px"
                  fallbackSrc="/fallback-img-3704x2304.png"
                  linkImageWrapperClassName={link ? 'py-[50%]' : undefined}
                  url={link && (isAbsoluteUrl(link) ? link : getRelativeUrl(link))}
                  target={isAbsoluteUrl(link) ? '_blank' : '_self'}
                  loading={prefetchImages ? 'eager' : 'lazy'}
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="font-bold">{title}</p>
                <Markdown className="text-small">{description || ''}</Markdown>
              </div>
            </div>
          );
        }}
      />
    </ContentWrapper>
  );
};

export default MiniSiteCircleFeature;
